import React, { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { PageTitleWithNav } from "../../Admin/modules/PageHeaders";
import { useNavigate } from "react-router-dom";
import {
  useGetCurrentMemberProfileQuery,
} from "../../../store/auth/authApi";
import {
  useGetMemberContributionSummaryQuery,
} from "../../../store/member/member-contribution";
import {
  useGetMemberLoanSummaryQuery,
  useGetPendingMemberLoanRequestsQuery,
  useGetApprovedMemberLoanListQuery,
  useGetDeclinedMemberLoanListQuery,
  useGetDisbursedMemberLoanListQuery,
} from "../../../store/member/member-loan";
import MemberAndStaffProfile from "../../Admin/modules/MemberAndStaffProfile";
import { useUser } from "../../../hooks";
import ProfileSkeletonLoader from "../../../components/Common/ProfileSkeletonLoader";
import MemberReferralCard from "./MemberReferralCard";

const MemberProfile = () => {
  const navigate = useNavigate();
  const userData = useSelector((state) => state.auth.userData);
  const user = useUser();

  // Fetch current member profile data
  const {
    data: profileData,
    isLoading: isLoadingProfile,
    error,
  } = useGetCurrentMemberProfileQuery();

  // Fetch contribution summary
  const {
    data: contributionSummary,
    isLoading: isLoadingContributionSummary,
  } = useGetMemberContributionSummaryQuery();

  // Fetch loan summary (skip if user not loaded)
  const {
    data: loanSummary,
    isLoading: isLoadingLoanSummary,
  } = useGetMemberLoanSummaryQuery(undefined, { 
    skip: !user?.member_id 
  });

  // Fetch loan lists for breakdown (only first page for totals)
  const { data: pendingLoans } = useGetPendingMemberLoanRequestsQuery(
    {
      page: 1,
      member_id: user?.member_id,
    },
    { skip: !user?.member_id }
  );

  const { data: approvedLoans } = useGetApprovedMemberLoanListQuery(
    {
      page: 1,
      member_id: user?.member_id,
    },
    { skip: !user?.member_id }
  );

  const { data: declinedLoans } = useGetDeclinedMemberLoanListQuery(
    {
      page: 1,
      member_id: user?.member_id,
    },
    { skip: !user?.member_id }
  );

  const { data: disbursedLoans } = useGetDisbursedMemberLoanListQuery(
    {
      page: 1,
      member_id: user?.member_id,
    },
    { skip: !user?.member_id }
  );

  // Get photo URL with cache-busting if available
  const getPhotoUrl = useCallback(() => {
    const currentData = profileData || userData;
    const photo = currentData?.photo || currentData?.profile_image || currentData?.avatar || "/Staff/avatar.svg";
    // Add cache-busting parameter if it's a URL (not a local path)
    if (photo && photo.startsWith('http')) {
      return photo.includes('?') ? `${photo}&t=${Date.now()}` : `${photo}?t=${Date.now()}`;
    }
    return photo;
  }, [profileData, userData]);

  // Calculate loan totals by status and format loans breakdown for tabs
  const { loansTotalsByStatus, loansBreakdown } = useMemo(() => {
    // Safely get loan lists with null safety
    const pendingList = Array.isArray(pendingLoans?.data?.company_loan_requests) 
      ? pendingLoans.data.company_loan_requests 
      : [];
    const approvedList = Array.isArray(approvedLoans?.data?.company_loan_requests) 
      ? approvedLoans.data.company_loan_requests 
      : [];
    const declinedList = Array.isArray(declinedLoans?.data?.company_loan_requests) 
      ? declinedLoans.data.company_loan_requests 
      : [];
    const disbursedList = Array.isArray(disbursedLoans?.data?.company_loan_requests) 
      ? disbursedLoans.data.company_loan_requests 
      : [];

    const pendingTotal = pendingList.reduce(
      (sum, loan) => sum + (parseFloat(loan?.amount) || 0),
      0
    );

    const approvedTotal = approvedList.reduce(
      (sum, loan) => sum + (parseFloat(loan?.amount) || 0),
      0
    );

    const declinedTotal = declinedList.reduce(
      (sum, loan) => sum + (parseFloat(loan?.amount) || 0),
      0
    );

    const disbursedTotal = disbursedList.reduce(
      (sum, loan) => sum + (parseFloat(loan?.amount) || 0),
      0
    );

    const totals = {
      pending: pendingTotal,
      approved: approvedTotal,
      declined: declinedTotal,
      disbursed: disbursedTotal,
    };

    // Format loans breakdown for LoansTab (expects object with status keys)
    const breakdown = {
      pending: pendingList,
      approved: approvedList,
      declined: declinedList,
      disbursed: disbursedList,
    };

    return {
      loansTotalsByStatus: totals,
      loansBreakdown: breakdown,
    };
  }, [pendingLoans, approvedLoans, declinedLoans, disbursedLoans]);

  // Prepare member details for MemberAndStaffProfile component
  const memberDetails = useMemo(() => {
    const currentData = profileData || userData;
    
    // Get contribution totals from summary API
    // total_contribution / total_contributed = grand total
    // total_savings = savings kind (net of withdrawals)
    // total_shares = shares kind
    const totalContribution =
      contributionSummary?.total_contribution ??
      contributionSummary?.total_contributed ??
      0;
    const totalSavings = contributionSummary?.total_savings ?? 0;
    const totalShares = contributionSummary?.total_shares ?? 0;

    // Get loan totals from loan summary API
    // Fallback to calculated disbursed total if loan summary not available
    const totalLoans = loanSummary?.loan_taken ?? loansTotalsByStatus?.disbursed ?? 0;

    // Get repayment status from loan summary API
    // Use nullish coalescing to ensure we get 0 if undefined/null
    const overdueRepaymentAmount = loanSummary?.overdue_repayment?.amount ?? loanSummary?.overdue_repayment ?? 0;
    const pendingRepaymentAmount = loanSummary?.pending_repayment?.amount ?? loanSummary?.pending_repayment ?? 0;
    const paidRepaymentAmount = loanSummary?.paid_amount ?? loanSummary?.paid_repayment?.amount ?? loanSummary?.paid_repayment ?? 0;

    return {
      ...currentData,
      first_name: currentData?.first_name || currentData?.name?.split(' ')[0] || '',
      last_name: currentData?.last_name || currentData?.name?.split(' ').slice(1).join(' ') || '',
      // Display membership number from API only — never fall back to internal member_id
      member_no: currentData?.member_no || "---",
      photo: getPhotoUrl(),
      phone: currentData?.phone || currentData?.phone_number || 'N/A',
      email: currentData?.email || 'N/A',
      address: currentData?.address || 'N/A',
      city: currentData?.city || {},
      country: currentData?.country || {},
      // Financial data from summary APIs
      contributions: {
        total: totalContribution,
        breakdown: [], // Will be loaded in ContributionsTab if needed
      },
      savings: {
        total: totalSavings,
        breakdown: [], // Will be loaded in SavingsTab if needed
      },
      shares: {
        total: totalShares,
        breakdown: [],
      },
      loans: {
        total: totalLoans,
        totals_by_status: loansTotalsByStatus,
        breakdown: loansBreakdown,
      },
      // Always include repayment_history for consistent UI
      repayment_history: {
        total_overdue: overdueRepaymentAmount,
        total_pending: pendingRepaymentAmount,
        total_paid: paidRepaymentAmount,
        breakdown: {}, // Will be loaded in RepaymentHistoryTab if needed
      },
      // Legacy fields for backwards compatibility
      total_contribution: totalContribution,
      loanTaken: totalLoans,
      overdue_repayment: overdueRepaymentAmount,
    };
  }, [
    profileData,
    userData,
    contributionSummary,
    loanSummary,
    loansTotalsByStatus,
    loansBreakdown,
    getPhotoUrl,
  ]);

  // Check if we're still loading any critical data
  const isLoading =
    isLoadingProfile || isLoadingContributionSummary || isLoadingLoanSummary;

  // Use profileData from API if available, fallback to userData from Redux
  const currentData = profileData || userData;

  if (isLoading) {
    return (
      <section className="mx-2 mx-lg-4 min-vh-100">
        <div className="py-1"></div>
        <ProfileSkeletonLoader />
      </section>
    );
  }

  if (error) {
    return (
      <section className="mx-2 mx-lg-4 min-vh-100">
        <div className="py-1"></div>
        <PageTitleWithNav title={"Profile"} action={() => navigate(-1)} />
        <div className="alert alert-danger">
          <h5>Error loading profile</h5>
          <p>Failed to load profile data. Please try again.</p>
        </div>
      </section>
    );
  }

  // Handle edit profile picture - navigate to edit page
  const handleEditPhoto = () => {
    navigate("/member/profile/edit");
  };

  // Handle edit profile - navigate to edit page
  const handleEditProfile = () => {
    navigate("/member/profile/edit");
  };

  return (
    <section className="mx-2 mx-lg-4 min-vh-100">
      <div className="py-1"></div>
      <MemberAndStaffProfile
        fullname={`${memberDetails.first_name} ${memberDetails.last_name}`.trim() || currentData?.name || "Member"}
        memberId={memberDetails.member_no}
        phone={memberDetails.phone}
        email={memberDetails.email}
        homeStreet={memberDetails.address}
        homeState={memberDetails.city?.name}
        photo={memberDetails.photo}
        memberDetails={memberDetails}
        isEditable={true}
        onEditPhoto={handleEditPhoto}
        onEditProfile={handleEditProfile}
      />
      <MemberReferralCard />
    </section>
  );
};

export default MemberProfile;

